import React from "react";
import { EMAIL_ADDRESS } from "../../constants/general";
import { Grid } from "../../UI/Grid";
import { Paper } from "../../UI/Paper";
import { Typography } from "../../UI/Typography";

const APPLICATION_URL = "https://app.roomsync.com";

const EmailTemplate = ({
  networkName,
  isPassCode,
  isJointLease,
  finalStepRequired,
  isApt,
  isUniquePassCode,
  invitationUrl,
}) => {
  let url = invitationUrl;

  let emailBody = null;

  if (!isApt && isUniquePassCode) {
    //    University - unique passcodes:
    emailBody = <UniversityUniquePassCode />;
    url = APPLICATION_URL;
  } else if (!isApt && finalStepRequired) {
    //    University - general passcode - RoomSync final step
    emailBody = <UniversityGeneralPassCodeFinalStep url={url} />;
  } else if (!isApt && !finalStepRequired) {
    //    University - General passcode - guiding to portal
    emailBody = <UniversityGeneralPassCodeNoFinalStep url={url} />;
  } else if (isApt && !isJointLease && isUniquePassCode) {
    //    Apartment - By the bed - unique passcodes
    emailBody = <ApartmentByTheBedUniquePassCode />;
    url = APPLICATION_URL;
  } else if (isApt && !isJointLease && isPassCode) {
    //    Apartment - By the bed - general passcodes
    emailBody = <ApartmentByTheBedGeneralPassCode url={url} />;
  } else if (isApt && isJointLease) {
    //    Apartment - joint lease
    emailBody = <ApartmentJointLease url={url} />;
  } else if (isApt) {
    emailBody = <ApartmentByTheBedGeneralPassCode url={url} />;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Suggested Email Language
      </Typography>
      <Typography variant="subtitle1">
        Copy and paste the suggested language below into an email and send to the residents who should be invited based
        on the status and unit type in the dropdown boxes above.
      </Typography>
      <Grid container mt={0} spacing={3} alignItems="center">
        <Grid item container alignItems="center">
          <Grid item xs={2}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px !important" }}>
              Subject:
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography variant="body2">Use RoomSync to find your roommate at {networkName}</Typography>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={2}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px !important" }}>
              Body:
            </Typography>
          </Grid>
          <Grid item xs={10}>
            {emailBody}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

const UniversityUniquePassCode = () => (
  <>
    <Typography variant="body2" gutterBottom>
      Hey there!
    </Typography>

    <Typography variant="body2" gutterBottom>
      We're excited to invite you to find your roommate and meet other residents through the RoomSync app on your
      iPhone, Android or laptop. Your code to gain access to RoomSync is [CODE].
    </Typography>

    <Typography variant="body2" gutterBottom>
      Click the below link from your preferred device to add the app and find your roommate:
    </Typography>

    <Typography variant="body2" gutterBottom>
      {APPLICATION_URL}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Once you have matched with your roommate on RoomSync, our office will receive the request and do our best to place
      you with them.
    </Typography>

    <Typography variant="body2" gutterBottom>
      If you have any questions about using the app, you can ask us or contact RoomSync at {EMAIL_ADDRESS}.
    </Typography>

    <Typography variant="body2" gutterBottom>
      Happy Syncing!
    </Typography>
  </>
);

const UniversityGeneralPassCodeNoFinalStep = ({ url }) => (
  <>
    <Typography variant="body2" gutterBottom>
      Hey there!
    </Typography>

    <Typography variant="body2" gutterBottom>
      We're excited to invite you to find your roommate and meet other residents through the RoomSync app on your
      iPhone, Android or laptop. Click the below link from your preferred device to add the app and find your roommate:
    </Typography>

    <Typography variant="body2" gutterBottom>
      {url}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Once you've matched with your roommate(s) on RoomSync, our office will receive a report with your request. We will
      do our best to place you with the roommate(s) you have chosen.
    </Typography>

    <Typography variant="body2" gutterBottom>
      If you have any questions about using the RoomSync app, you can ask a member of our team, or, reach out to
      RoomSync at {EMAIL_ADDRESS}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Happy Syncing!
    </Typography>
  </>
);

const UniversityGeneralPassCodeFinalStep = ({ url }) => (
  <>
    <Typography variant="body2" gutterBottom>
      Hey there!
    </Typography>

    <Typography variant="body2" gutterBottom>
      We're excited to invite you to find your roommate and meet other residents through the RoomSync app on your
      iPhone, Android or laptop. Click the below link from your preferred device to add the app and find your roommate:
    </Typography>

    <Typography variant="body2" gutterBottom>
      {url}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Once you have matched with your roommate on RoomSync, you will need to make your request official by requesting
      your roommate through our [Housing Portal Site].
    </Typography>

    <Typography variant="body2" gutterBottom>
      If you have any questions about using the app, you can ask us or contact RoomSync at {EMAIL_ADDRESS}.
    </Typography>

    <Typography variant="body2" gutterBottom>
      Happy Syncing!
    </Typography>
  </>
);

const ApartmentByTheBedUniquePassCode = () => (
  <>
    <Typography variant="body2" gutterBottom>
      Hey there!
    </Typography>

    <Typography variant="body2" gutterBottom>
      We're excited to invite you to find your roommate and meet other residents through the RoomSync app on your
      iPhone, Android or laptop. Your code to gain access to RoomSync is [CODE].
    </Typography>

    <Typography variant="body2" gutterBottom>
      Click the below link from your preferred device to add the app and find your roommate:
    </Typography>

    <Typography variant="body2" gutterBottom>
      {APPLICATION_URL}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Once you have matched with your roommate on RoomSync, our office will receive the request and do our best to place
      you with them.
    </Typography>

    <Typography variant="body2" gutterBottom>
      If you have any questions about using the app, you can ask us or contact RoomSync at {EMAIL_ADDRESS}.
    </Typography>

    <Typography variant="body2" gutterBottom>
      Happy Syncing!
    </Typography>
  </>
);

const ApartmentByTheBedGeneralPassCode = ({ url }) => (
  <>
    <Typography variant="body2" gutterBottom>
      Hey there!
    </Typography>

    <Typography variant="body2" gutterBottom>
      We're excited to invite you to find your roommate and meet other residents through the RoomSync app on your
      iPhone, Android or laptop. Click the below link from your preferred device to add the app and find your roommate:
    </Typography>

    <Typography variant="body2" gutterBottom>
      {url}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Once you've matched with your roommate(s) on RoomSync, our office will receive a report with your request. We will
      do our best to place you with the roommate(s) you have chosen.
    </Typography>

    <Typography variant="body2" gutterBottom>
      If you have any questions about using the RoomSync app, you can ask a member of our team, or, reach out to
      RoomSync at {EMAIL_ADDRESS}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Happy Syncing!
    </Typography>
  </>
);

const ApartmentJointLease = ({ url }) => (
  <>
    <Typography variant="body2" gutterBottom>
      Hey there!
    </Typography>

    <Typography variant="body2" gutterBottom>
      We're excited to invite you to find your roommate and meet other residents through the RoomSync app on your
      iPhone, Android or laptop. Click the below link from your preferred device to add the app and find your roommate:
    </Typography>

    <Typography variant="body2" gutterBottom>
      {url}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Once you've matched with your roommate(s) on RoomSync, return to the leasing office to sign a lease together at
      our community.
    </Typography>

    <Typography variant="body2" gutterBottom>
      If you have any questions about using the RoomSync app, you can ask a member of our team, or, reach out to
      RoomSync at {EMAIL_ADDRESS}
    </Typography>

    <Typography variant="body2" gutterBottom>
      Happy Syncing!
    </Typography>
  </>
);

export default EmailTemplate;
